import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { CreateSubjectDocumentRequest, ParsedError } from "../../interfaces";
import { ActivityButton } from "../../components/Common";
import { PDF_ACCEPT } from "../../constants";
import { MediaDto } from "../../interfaces/media";
import { applyServerFormErrors, flattenErrorMessage, formatFileSize } from "../../services";

import {
  useGetSubjectDocumentQuery,
  useAddSubjectDocumentMutation,
  useUpdateSubjectDocumentMutation,
  useUploadSubjectDocumentMutation,
  useDownloadSubjectDocumentMutation,
} from "../../store/api/subjectDocumentApi";
import { toast } from "react-toastify";
import { ErrorHandlingTypeEnum } from "../../enums";

type TProps = {
  id?: string | null;
  subjectId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSubjectDocumentModal: React.FC<TProps> = ({
  id,
  subjectId,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateSubjectDocumentRequest>();
  const [uploadResponse, setUploadResponse] = useState<MediaDto | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    data: subjectDocument,
    isError: isErrorDocument,
    isLoading: isLoadingDocument,
  } = useGetSubjectDocumentQuery(id as string, { skip: !id });

  const [
    addSubjectDocument,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddSubjectDocumentMutation();

  const [
    updateSubjectDocument,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateSubjectDocumentMutation();

  const [
    uploadSubjectDocument,
    { isLoading: isLoadingUpload, isError: isErrorUpload, error: errorUpload },
  ] = useUploadSubjectDocumentMutation();

  const [downloadSubjectDocument, { isLoading: downloading }] =
    useDownloadSubjectDocumentMutation();

  const uploadSubjectDocumentResponse = uploadResponse;

  //#region form
  const form = useFormik<CreateSubjectDocumentRequest>({
    enableReinitialize: true,
    initialValues: {
      title: {
        en: request?.title?.en || "",
        ar: request?.title?.ar || "",
      },
      fileId: "",
      sequenceNumber: request?.sequenceNumber || 0,
      subjectId: subjectId,
    },
    validationSchema: Yup.object({
      title: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      fileId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "fileId",
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateSubjectDocument({
            id,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        } else {
          await addSubjectDocument({
            ...values,
            sequenceNumber: Number(values.sequenceNumber),
          }).unwrap();
        }
      } catch {
        // Server errors are handled by baseApi toast + the form error effect
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (id && subjectDocument) {
      form.setValues({
        title: subjectDocument.title,
        fileId: subjectDocument?.file?.id || "",
        sequenceNumber:
          form.values.sequenceNumber || subjectDocument?.sequenceNumber || 0,
        subjectId: subjectId,
      });
    }
  }, [subjectDocument, id, isLoadingDocument]);

  useEffect(() => {
    if (!id) {
      setUploadResponse(null);
      setUploadError(null);
      form.resetForm({
        values: {
          title: { en: "", ar: "" },
          fileId: "",
          sequenceNumber: 0,
          subjectId: subjectId,
        },
      });
    }
  }, [id, subjectId]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingUpload && isErrorUpload) _error = errorUpload as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      applyServerFormErrors(form, _error.error);
      if (typeof _error.error?.fileId === "string") {
        setUploadError(_error.error.fileId);
      }
    }
  }, [isLoadingAdd, isLoadingUpdate, isLoadingUpload]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    setUploadResponse(null);
    setUploadError(null);
    onCloseClick(!show);
  };

  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const pdfFile = files[0];
      if (!pdfFile.name.match(/\.(pdf)$/)) {
        toast.error(t("selectPdfOnly"), {
          autoClose: 3000,
        });
        return false;
      }
      setUploadError(null);
      form.setFieldError("fileId", undefined);
      uploadSubjectDocument({
        file: pdfFile,
        subjectDocumentId: id ?? undefined,
      })
        .unwrap()
        .then((res) => {
          setUploadResponse(res);
          form.setFieldValue("fileId", res.id);
          form.setFieldError("fileId", undefined);
        })
        .catch((err) => {
          const msg =
            flattenErrorMessage(
              (err as any)?.error || (err as any)?.data?.message,
            ) ||
            t("uploadError") ||
            "An error occurred";
          setUploadError(msg);
          toast.error(msg, { autoClose: 3000 });
        });
      event.target.value = "";
    }
  };

  const onDownload = async () => {
    if (!id) return;
    try {
      const blob = await downloadSubjectDocument(id).unwrap();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        subjectDocument?.file?.name ||
          uploadSubjectDocumentResponse?.name ||
          "downloaded.pdf",
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(t("downloadingError"), { autoClose: 3000 });
    }
  };

  //#endregion

  //#region render
  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "document" })
              : t("addItem", { item: "document" })}
          </ModalHeader>
          <ModalBody>
            <div
              className="text-center p-4 mb-3"
              style={{
                border: `solid 1px ${
                  form.errors.fileId || uploadError ? "#f06548" : "#ced4da"
                }`,
                borderRadius: "5px",
              }}
            >
              <label htmlFor="subject-note-input" className="cursor-pointer">
                {id && subjectDocument ? (
                  <i className="ri-file-pdf-line align-bottom text-danger display-5 cursor-pointer"></i>
                ) : (
                  <i className="ri-file-upload-line align-bottom text-danger display-5 cursor-pointer"></i>
                )}
                {!uploadSubjectDocumentResponse &&
                  (!id || !subjectDocument?.file) && (
                    <h6 className="fs-12 folder-name mt-1 cursor-pointer">
                      {t("clickToUpload")}
                    </h6>
                  )}
                <h6 className="fs-12 text-mute cursor-pointer">
                  {(typeof uploadError === "string" || form.errors.fileId) && (
                    <Label className="invalid-feedback d-block">
                      {uploadError || (form.errors.fileId as string)}
                    </Label>
                  )}
                </h6>
                <div className=" text-muted">
                  {((id && subjectDocument?.file) ||
                    uploadSubjectDocumentResponse?.path) && (
                    <React.Fragment>
                      <span className="me-auto text-center">
                        <b>{t("name")}</b>{" "}
                        {uploadSubjectDocumentResponse?.name ||
                          subjectDocument?.file?.name}
                      </span>
                      <br />
                      <span className="me-auto">
                        <b>{t("size")}</b>{" "}
                        {formatFileSize(
                          Number(uploadSubjectDocumentResponse?.size) ||
                            (id && subjectDocument?.file
                              ? Number(subjectDocument.file.size)
                              : 0) ||
                            0,
                        )}
                      </span>
                    </React.Fragment>
                  )}
                  <br />
                  <span>{isLoadingUpload && <Spinner size={"sm"} />}</span>
                </div>
              </label>
              <br />
              {((id && subjectDocument?.file?.path) ||
                uploadSubjectDocumentResponse?.path) &&
                !isLoadingUpload && (
                  <div>
                    {downloading ? (
                      <span className="me-auto">
                        <Spinner size={"sm"} /> {t("downloading")}
                      </span>
                    ) : (
                      <Button
                        onClick={onDownload}
                        className="btn btn-sm btn-primary"
                      >
                        {t("download")}
                      </Button>
                    )}
                  </div>
                )}
              <Input
                id="subject-note-input"
                type="file"
                className="profile-foreground-img-file-input d-none"
                accept={PDF_ACCEPT}
                onChange={onChangeFile}
                disabled={isLoadingUpload}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="title.en" className="form-label">
                {t("titleEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={isErrorDocument}
                autoComplete="off"
                id="title.en"
                name="title.en"
                value={form.values?.title.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.en && form.errors.title?.en ? true : false
                }
              />
              {form.touched.title?.en && form.errors.title?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="title.ar" className="form-label">
                {t("titleAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={isErrorDocument}
                autoComplete="off"
                id="title.ar"
                name="title.ar"
                value={form.values.title?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.title?.ar && form.errors.title?.ar ? true : false
                }
              />
              {form.touched.title?.ar && form.errors.title?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.title?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                isErrorDocument ||
                isLoadingUpload
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
